import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Wrench, MessageSquare, CalendarClock, Image as ImgIcon, Send, Plus, Trash2, X, Upload, Users, MapPin, Compass } from "lucide-react";
import { useAuth } from "../lib/auth";
import { compressImageTo100KB } from "../lib/imageCompress";

const LEADER_TYPES = ["Organizer", "Tour Leader", "Activity Leader", "Guest Speaker", "Coordinator"];
const EMPTY_EVENT = { title: "", subtitle: "", price: 0, duration: "", description: "", date: "", location: "", images: [], itinerary: [] };
const EMPTY_ITIN = { day: "", time: "", activity: "", leader_name: "", leader_type: "" };

// Combined staff view for Service Requests, Threads, Event manager (BDOM/PCWM).
export default function ParticipantOps() {
  const { user } = useAuth();
  const role = user.effective_role;
  const isGehm = role === "GEHM" || role === "FOUNDER" || role === "BDOM";
  const canReplyProjects = role === "PCWM" || role === "FOUNDER";
  const canReplyLodging = role === "GEHM" || role === "FOUNDER";
  const canReplyFeedback = role === "BDOM" || role === "FOUNDER";
  const canReplyDisclaimer = role === "SMCM" || role === "FOUNDER";
  const canManageEvents = ["FOUNDER","BDOM","PCWM"].includes(role);

  const [tab, setTab] = useState("services");
  const [reqs, setReqs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [threads, setThreads] = useState({ projects: [], lodging: [], feedback: [], disclaimer: [] });
  const [events, setEvents] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const [newEvent, setNewEvent] = useState({ ...EMPTY_EVENT });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      if (isGehm) {
        const [{ data: r }, { data: s }] = await Promise.all([api.get("/service-requests"), api.get("/service-requests/summary")]);
        setReqs(r); setSummary(s);
      }
      const kinds = ["projects","lodging","feedback","disclaimer"];
      const results = await Promise.all(kinds.map(k => api.get(`/threads?kind=${k}`).catch(() => ({ data: [] }))));
      const th = {}; kinds.forEach((k,i) => th[k] = results[i].data);
      setThreads(th);
      const { data: ev } = await api.get("/events");
      setEvents(ev);
    } catch (e) {
      console.debug("ParticipantOps load failed:", e?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const patchReq = async (id, patch) => { await api.patch(`/service-requests/${id}`, patch); load(); };
  const delReq = async (id) => {
    try {
      await api.delete(`/service-requests/${id}`);
      toast.success("Request deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const reply = async (kind, id) => {
    const text = (replyDraft[id] || "").trim();
    if (!text) return toast.error("Reply is empty");
    await api.post(`/threads/${id}/reply`, { text });
    toast.success("Reply sent");
    setReplyDraft({ ...replyDraft, [id]: "" });
    load();
  };
  const createEvent = async () => {
    if (!newEvent.title.trim()) return toast.error("Title required");
    const cleanItin = newEvent.itinerary.filter((it) => (it.activity || "").trim() || (it.leader_name || "").trim());
    await api.post("/events", {
      ...newEvent,
      price: Number(newEvent.price) || 0,
      itinerary: cleanItin,
      picture_url: newEvent.images[0] || "",
    });
    toast.success("Event created");
    setNewEvent({ ...EMPTY_EVENT });
    load();
  };
  const delEvent = async (id) => { await api.delete(`/events/${id}`); load(); };

  const addItineraryRow = () => setNewEvent((e) => ({ ...e, itinerary: [...e.itinerary, { ...EMPTY_ITIN }] }));
  const patchItin = (idx, patch) => setNewEvent((e) => ({ ...e, itinerary: e.itinerary.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const rmItin = (idx) => setNewEvent((e) => ({ ...e, itinerary: e.itinerary.filter((_, i) => i !== idx) }));

  const handleImagePick = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    for (const f of Array.from(files)) {
      try {
        const compressed = await compressImageTo100KB(f);
        const fd = new FormData();
        fd.append("file", compressed, compressed.name || "event.jpg");
        const { data } = await api.post("/events/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || err?.message || "Upload failed"}`);
      }
    }
    if (added.length) {
      setNewEvent((e) => ({ ...e, images: [...e.images, ...added] }));
      toast.success(`${added.length} picture(s) added`);
    }
    setUploading(false);
  };
  const rmImage = (i) => setNewEvent((e) => ({ ...e, images: e.images.filter((_, k) => k !== i) }));

  const TABS = [
    { key: "services", label: "Service Requests", icon: Wrench, visible: isGehm },
    { key: "projects", label: "Projects", icon: MessageSquare, visible: canReplyProjects },
    { key: "lodging", label: "Lodging", icon: MessageSquare, visible: canReplyLodging },
    { key: "feedback", label: "Feedback", icon: MessageSquare, visible: canReplyFeedback },
    { key: "disclaimer", label: "Media consents", icon: ImgIcon, visible: canReplyDisclaimer },
    { key: "events", label: "Events", icon: CalendarClock, visible: canManageEvents },
    { key: "tours", label: "Country Tours", icon: Compass, visible: canManageEvents },
  ].filter((t) => t.visible);

  const threadListProps = { threads, replyDraft, setReplyDraft, reply };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Participant Ops</div>
        <h1 className="text-2xl sm:text-3xl font-semibold">Participant Operations</h1>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(({key, label, icon: Icon}) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`op-tab-${key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap ${tab === key ? "bg-brand text-white" : "border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
            <Icon size={12}/> {label}
          </button>
        ))}
      </div>

      {tab === "services" && isGehm && (
        <>
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Total</div><div className="text-2xl font-display font-semibold">{summary.total_requests}</div></div>
              <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Pending</div><div className="text-2xl font-display font-semibold">{summary.pending}</div></div>
              <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Done</div><div className="text-2xl font-display font-semibold">{summary.done}</div></div>
              <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Paid Rs</div><div className="text-2xl font-display font-semibold text-emerald-700">{summary.paid_total}</div></div>
              <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Due Rs</div><div className="text-2xl font-display font-semibold text-red-700">{summary.due_total}</div></div>
            </div>
          )}
          <div className="aii-card overflow-x-auto"><table className="aii-table"><thead><tr><th>Participant</th><th>Service</th><th>Qty</th><th>Total</th><th>Status</th><th>Payment</th><th>Notes</th><th></th></tr></thead><tbody>
            {reqs.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[color:var(--aii-muted)]">No requests.</td></tr>}
            {reqs.map((r) => (
              <tr key={r.id} data-testid={`sr-row-${r.id}`}>
                <td>{r.participant_name}</td><td>{r.service_label}</td>
                <td>
                  <input type="number" min="0" step="0.5" defaultValue={r.quantity}
                    onBlur={(e) => {
                      const q = parseFloat(e.target.value);
                      if (!isNaN(q) && q !== r.quantity) patchReq(r.id, { quantity: q });
                    }}
                    data-testid={`sr-qty-${r.id}`}
                    className="w-16 text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded"/>
                </td>
                <td>
                  {r.free ? "FREE" : (
                    <input type="number" min="0" step="0.01" defaultValue={r.total}
                      onBlur={(e) => {
                        const t = parseFloat(e.target.value);
                        if (!isNaN(t) && t !== r.total) patchReq(r.id, { total: t });
                      }}
                      data-testid={`sr-total-${r.id}`}
                      className="w-20 text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded"
                      title="Override the total — e.g. courtesy discount"/>
                  )}
                </td>
                <td>
                  <select value={r.status} onChange={(e) => patchReq(r.id, { status: e.target.value })} data-testid={`sr-status-${r.id}`} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded">
                    {["pending","in_progress","done","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td>
                  <select value={r.payment_status} onChange={(e) => patchReq(r.id, { payment_status: e.target.value })} data-testid={`sr-pay-${r.id}`} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded">
                    {["due","paid","not_applicable","waived"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="text-xs text-[color:var(--aii-muted)] max-w-[180px] truncate" title={r.notes}>{r.notes}</td>
                <td>
                  {role === "FOUNDER" && (
                    <button
                      onClick={() => { if (window.confirm(`Delete request from ${r.participant_name}?`)) delReq(r.id); }}
                      data-testid={`sr-del-${r.id}`}
                      className="text-red-600 p-1.5 hover:bg-red-50 rounded"
                      title="Delete request (Founder only)">
                      <Trash2 size={13}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody></table></div>
        </>
      )}

      {tab === "projects" && canReplyProjects && <ThreadList kind="projects" {...threadListProps}/>}
      {tab === "lodging" && canReplyLodging && <ThreadList kind="lodging" {...threadListProps}/>}
      {tab === "feedback" && canReplyFeedback && <ThreadList kind="feedback" {...threadListProps}/>}
      {tab === "disclaimer" && canReplyDisclaimer && (
        <div className="aii-card overflow-x-auto"><table className="aii-table"><thead><tr><th>Participant</th><th>Decision</th><th>When</th></tr></thead><tbody>
          {threads.disclaimer.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-[color:var(--aii-muted)]">No consents yet.</td></tr>}
          {threads.disclaimer.map((t) => <tr key={t.id}><td>{t.participant_name}</td><td>{t.accept ? <span className="text-emerald-700 font-semibold">ACCEPTED</span> : <span className="text-red-700 font-semibold">REJECTED</span>}</td><td className="text-xs">{new Date(t.created_at).toLocaleString()}</td></tr>)}
        </tbody></table></div>
      )}

      {tab === "events" && canManageEvents && (
        <>
          <div className="aii-card p-5 space-y-4">
            <h3 className="font-display font-semibold">Add upcoming event</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className="input-aii" placeholder="Title *" value={newEvent.title} onChange={(e)=>setNewEvent({...newEvent, title:e.target.value})} data-testid="ev-title"/>
              <input className="input-aii" placeholder="Subtitle / tagline" value={newEvent.subtitle} onChange={(e)=>setNewEvent({...newEvent, subtitle:e.target.value})} data-testid="ev-subtitle"/>
              <input className="input-aii" placeholder="Location (e.g. Rishikesh)" value={newEvent.location} onChange={(e)=>setNewEvent({...newEvent, location:e.target.value})} data-testid="ev-location"/>
              <input className="input-aii" placeholder="Duration (e.g. 1 day / 2N-3D)" value={newEvent.duration} onChange={(e)=>setNewEvent({...newEvent, duration:e.target.value})} data-testid="ev-duration"/>
              <input className="input-aii" type="number" placeholder="Price (Rs)" value={newEvent.price} onChange={(e)=>setNewEvent({...newEvent, price:e.target.value})} data-testid="ev-price"/>
              <input className="input-aii" type="date" placeholder="Date" value={newEvent.date} onChange={(e)=>setNewEvent({...newEvent, date:e.target.value})} data-testid="ev-date"/>
              <textarea className="input-aii sm:col-span-2" rows={3} placeholder="Description — sell the experience!" value={newEvent.description} onChange={(e)=>setNewEvent({...newEvent, description:e.target.value})} data-testid="ev-desc"/>
            </div>

            {/* Pictures */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Pictures ({newEvent.images.length}) &middot; auto-compressed to &le;100 KB each</div>
                <label className="text-xs px-2 py-1 border border-dashed border-[color:var(--aii-border)] rounded cursor-pointer hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1" data-testid="ev-add-pics">
                  <Upload size={12}/> {uploading ? "Uploading…" : "Add pictures"}
                  <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e)=>handleImagePick(e.target.files)}/>
                </label>
              </div>
              {newEvent.images.length === 0 ? (
                <div className="border-2 border-dashed border-[color:var(--aii-border)] rounded p-4 text-center text-xs text-[color:var(--aii-muted)]">No pictures yet — the classic card needs at least one hero image.</div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {newEvent.images.map((u, i) => (
                    <div key={`img-${u.slice(-24)}-${i}`} className="relative aspect-square rounded overflow-hidden border border-[color:var(--aii-border)] group">
                      <img src={u} alt="" className="w-full h-full object-cover"/>
                      {i === 0 && <div className="absolute top-1 left-1 bg-brand text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded">Hero</div>}
                      <button onClick={()=>rmImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`ev-pic-rm-${i}`}><X size={10}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Itinerary */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Itinerary / Schedule</div>
                <button onClick={addItineraryRow} className="text-xs px-2 py-1 border border-dashed border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1" data-testid="ev-add-itin"><Plus size={12}/> Add row</button>
              </div>
              {newEvent.itinerary.length === 0 && <div className="text-xs text-[color:var(--aii-muted)]">No schedule rows yet.</div>}
              <div className="space-y-2">
                {newEvent.itinerary.map((it, i) => (
                  <div key={`itin-${i}-${it.day || ''}-${(it.activity || '').slice(0,10)}`} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded">
                    <input className="input-aii col-span-3 sm:col-span-2 text-xs" placeholder="Day / Date" value={it.day} onChange={(e)=>patchItin(i,{day:e.target.value})} data-testid={`itin-day-${i}`}/>
                    <input className="input-aii col-span-3 sm:col-span-1 text-xs" placeholder="Time" value={it.time} onChange={(e)=>patchItin(i,{time:e.target.value})} data-testid={`itin-time-${i}`}/>
                    <input className="input-aii col-span-6 sm:col-span-4 text-xs" placeholder="Activity" value={it.activity} onChange={(e)=>patchItin(i,{activity:e.target.value})} data-testid={`itin-act-${i}`}/>
                    <input className="input-aii col-span-6 sm:col-span-2 text-xs" placeholder="Leader name" value={it.leader_name} onChange={(e)=>patchItin(i,{leader_name:e.target.value})} data-testid={`itin-lead-${i}`}/>
                    <select className="input-aii col-span-5 sm:col-span-2 text-xs" value={it.leader_type} onChange={(e)=>patchItin(i,{leader_type:e.target.value})} data-testid={`itin-role-${i}`}>
                      <option value="">Role…</option>
                      {LEADER_TYPES.map((r)=> <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button onClick={()=>rmItin(i)} className="col-span-1 text-red-600 flex justify-center" data-testid={`itin-rm-${i}`}><Trash2 size={14}/></button>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={createEvent} disabled={uploading} data-testid="ev-create" className="px-4 py-2 bg-brand text-white rounded flex items-center gap-2 disabled:opacity-50"><Plus size={13}/> Create event</button>
          </div>

          <div className="space-y-3">
            {events.map((e) => (
              <div key={e.id} className="aii-card p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {(e.images?.[0] || e.picture_url) && <img src={e.images?.[0] || e.picture_url} alt="" className="w-14 h-14 object-cover rounded"/>}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-2 flex-wrap">
                      <span>{e.date || "no date"}</span>
                      <span>&middot; Rs {e.price}</span>
                      {e.location && <span className="flex items-center gap-0.5"><MapPin size={10}/>{e.location}</span>}
                      <span className="flex items-center gap-0.5"><ImgIcon size={10}/>{(e.images?.length) || (e.picture_url ? 1 : 0)}</span>
                      <span className="flex items-center gap-0.5"><CalendarClock size={10}/>{e.itinerary?.length || 0}</span>
                      <span className="flex items-center gap-0.5"><Users size={10}/>{e.booked_names?.length || 0}</span>
                    </div>
                  </div>
                </div>
                {role === "FOUNDER" && (
                  <button onClick={()=>delEvent(e.id)} className="text-red-600 p-2" data-testid={`ev-del-${e.id}`}><Trash2 size={14}/></button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {tab === "tours" && canManageEvents && <ToursPanel/>}
    </div>
  );
}

// ---------------- Thread list (extracted to module scope to avoid recreating
// a fresh component identity on every render of ParticipantOps). ----------------
function ThreadList({ kind, threads, replyDraft, setReplyDraft, reply }) {
  return (
    <div className="space-y-3">
      {(threads[kind] || []).length === 0 && <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No {kind} messages.</div>}
      {threads[kind].map((t) => (
        <div key={t.id} className="aii-card p-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div><div className="font-semibold">{t.subject}</div><div className="text-xs text-[color:var(--aii-muted)]">{t.participant_name} · {new Date(t.created_at).toLocaleString()}</div></div>
            <div className="flex items-center gap-2 text-xs">
              <span className="uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-slate-100">{t.status}</span>
              {t.rating != null && <span className={`px-2 py-0.5 rounded ${t.rating <= 3 ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>★ {t.rating}/5</span>}
              {t.escalated_to_bdom && <span className="bg-red-600 text-white px-2 py-0.5 rounded">ESCALATED</span>}
            </div>
          </div>
          <div className="space-y-2">
            {t.messages?.map((m, i) => (
              <div key={`${t.id}-msg-${i}`} className={`text-sm p-2 rounded ${m.by === "staff" ? "bg-[color:var(--aii-green-tint)]" : "bg-slate-50"}`}>
                <div className="text-xs text-[color:var(--aii-muted)]">{m.by === "staff" ? `${m.by_name} (${m.by_role})` : t.participant_name} · {new Date(m.at).toLocaleString()}</div>
                <div>{m.text}</div>
              </div>
            ))}
          </div>
          {kind !== "disclaimer" && t.status === "open" && (
            <div className="mt-3 flex gap-2">
              <input placeholder="Reply…" value={replyDraft[t.id] || ""} onChange={(e) => setReplyDraft({ ...replyDraft, [t.id]: e.target.value })} className="input-aii flex-1" data-testid={`reply-input-${t.id}`}/>
              <button onClick={() => reply(kind, t.id)} data-testid={`reply-send-${t.id}`} className="px-3 py-2 bg-brand text-white rounded flex items-center gap-1"><Send size={12}/> Send</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------- Country Tours (BDOM) ----------------
function ToursPanel() {
  // Role gating — Founder-only actions (like delete) need the current
  // user's role. This was previously read from an outer scope which
  // Node/JS treated as `undefined`, crashing the tab when a BDOM opened
  // it. Now scoped locally so it's stable.
  const { user } = useAuth();
  const role = user.effective_role;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ country: "", city: "", title: "", description: "", price: 0, currency: "USD", duration: "", image_url: "", min_pax: 4 });
  const COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Uganda", "Nigeria", "Kenya", "Bolivia", "Thailand", "Vietnam"];
  const load = async () => {
    const { data } = await api.get("/country-tours");
    setRows(data.tours || []);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.country || !form.city.trim() || !form.title.trim()) { toast.error("Country, city and title are required"); return; }
    try {
      await api.post("/country-tours", { ...form, price: parseFloat(form.price) || 0, min_pax: parseInt(form.min_pax) || 4 });
      toast.success("Tour published");
      setForm({ country: "", city: "", title: "", description: "", price: 0, currency: "USD", duration: "", image_url: "", min_pax: 4 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Retire this tour?")) return;
    await api.delete(`/country-tours/${id}`);
    load();
  };
  return (
    <>
      <div className="aii-card p-5 space-y-3">
        <h3 className="font-display font-semibold">Publish a new country tour</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select className="input-aii" value={form.country} onChange={e=>setForm({...form, country: e.target.value})} data-testid="ct-country">
            <option value="">Country…</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input-aii" placeholder="City" value={form.city} onChange={e=>setForm({...form, city: e.target.value})} data-testid="ct-city"/>
          <input className="input-aii sm:col-span-2" placeholder="Title *" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} data-testid="ct-title"/>
          <textarea className="input-aii sm:col-span-2" rows={2} placeholder="Description" value={form.description} onChange={e=>setForm({...form, description: e.target.value})}/>
          <input className="input-aii" placeholder="Image URL (optional)" value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})}/>
          <input className="input-aii" placeholder="Duration e.g. 3 days" value={form.duration} onChange={e=>setForm({...form, duration: e.target.value})}/>
          <input className="input-aii" type="number" placeholder="Price" value={form.price} onChange={e=>setForm({...form, price: e.target.value})}/>
          <select className="input-aii" value={form.currency} onChange={e=>setForm({...form, currency: e.target.value})}>
            {["USD","INR","EUR","GBP","AUD"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input-aii" type="number" placeholder="Min pax" value={form.min_pax} onChange={e=>setForm({...form, min_pax: e.target.value})}/>
        </div>
        <button onClick={create} data-testid="ct-create" className="px-4 py-2 bg-brand text-white rounded flex items-center gap-2"><Plus size={13}/> Publish tour</button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No tours yet. Publish one above to surface it in participants&rsquo; dashboards.</div>}
        {rows.map((t) => (
          <div key={t.id} className="aii-card p-3 flex items-center gap-3">
            {t.image_url ? <img src={t.image_url} alt="" className="w-16 h-16 rounded object-cover"/> : <div className="w-16 h-16 rounded bg-slate-100 flex items-center justify-center text-[color:var(--aii-muted)]"><Compass size={22}/></div>}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{t.title}</div>
              <div className="text-xs text-[color:var(--aii-muted)] flex flex-wrap gap-2">
                <span>{t.country} · {t.city}</span>
                <span>· {t.currency} {t.price}</span>
                <span>· min {t.min_pax} pax</span>
                <span>· {t.inquiry_count || 0} interested</span>
              </div>
            </div>
            {role === "FOUNDER" && (
              <button onClick={()=>del(t.id)} className="text-red-600 p-2" data-testid={`ct-del-${t.id}`}><Trash2 size={14}/></button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
